import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";

import styles from "./PreventiveCareCriteriaPopup.module.scss";

interface IPreventiveActivitiesCriteriaPopupProps {
    onClose?: () => void;
}

const MONTHS_IN_A_YEAR = 12;

function PreventiveCareCriteriaPopup(props: IPreventiveActivitiesCriteriaPopupProps): JSX.Element {
    const { getText } = useTranslation();

    function getAgeLabel(ageFrom: number, ageTo?: number): string {
        if (!ageTo) {
            return getText("RecallList_PreventiveCareCriteriaAgeFromLabel", { ageFrom });
        }

        // Check if age is under 5 years old, and convert years to months
        if (ageTo < 5) {
            return getText("RecallList_PreventiveCareCriteriaAgeFromToMonthsLabel", { ageFrom: ageFrom * MONTHS_IN_A_YEAR, ageTo: ageTo * MONTHS_IN_A_YEAR });
        }

        return getText("RecallList_PreventiveCareCriteriaAgeFromToYearsLabel", { ageFrom, ageTo });
    }

    function getGenderLabel(genders?: Guid[]): string {
        return genders?.map(lookupId => cacheStore.GetLocalizedValueByLookupId(lookupId)).join(", ");
    }

    function renderFooter(): JSX.Element {
        return (
            <div className="p-footer">
                <div className="flex-container flex-end">
                    <button
                        className="btn btn-primary margin-sm-left"
                        onClick={() => props.onClose?.()}
                    >
                        {getText("Ok_Label")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Modal
            showX
            centerContent
            draggable
            title={getText("RecallList_PreventiveActivitiesCriteriaPupopTitle")}
            width={750}
            maxHeight="620px"
            top={1}
            showOverlay
            onClose={() => props.onClose?.()}
            footer={renderFooter()}
        >
            <div className={`${styles.modalContentWrapper} margin-xs-top`}>
                <div>
                    <p>{getText("RecallList_PreventiveActivitiesCriteriaPupopSubTitle")}</p>
                </div>
                <div className={styles.tableContainer}>
                    <table className={styles.preventiveTable}>
                        <thead>
                            <tr>
                                <th className={styles.borderRightNone}>{getText("PreventiveActivityLabel").toUpperCase()}</th>
                                <th className={`${styles.borderRightNone} ${styles.borderLeftNone} ${styles.age}`}>{getText("AgeLabel").toUpperCase()}</th>
                                <th className={`${styles.borderRightNone} ${styles.borderLeftNone}`}>{getText("SexAtBirthLabel").toUpperCase()}</th>
                                <th className={styles.borderLeftNone}>{getText("RecallList_ExclusionCodesLabel").toUpperCase()}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cacheStore.PreventiveCareCriteriaList.map(p => (
                                <tr key={p.Id}>
                                    <td className={styles.borderRightNone}>{cacheStore.GetLocalizedValueByLookupId(p.NameLookupId)}</td>
                                    <td className={`${styles.borderRightNone} ${styles.borderLeftNone}`}>{getAgeLabel(p.AgeFrom, p.AgeTo)}</td>
                                    <td className={`${styles.borderRightNone} ${styles.borderLeftNone}`}>{getGenderLabel(p.GenderLookupIds)}</td>
                                    <td className={styles.borderLeftNone}>{p.ExclusionBillingCode}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
}

export { PreventiveCareCriteriaPopup };
